import { useCallback, useEffect, useMemo, useState } from 'react'
import { BellRing, CalendarClock, Handshake, Link2, Link2Off } from 'lucide-react'
import { Link } from '../router.js'
import { Drawer, Select, adminMutate, apiGet, usePermissions, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { SOURCE_LABELS, heureFr, jourFr } from './lib.js'
import {
  EVT_RATTACHEMENTS, alerteDuCas, idDuCas, libelleSousLigne, nuitCourte, phraseConsequence, phraseProchaineAssociation, phraseSeules, seulesAVenir,
  texteMethode, texteVerdict, titrePanneauAVenir,
  type AssociationCourte, type EtatAlerteCas, type ItemARattacher, type ListeRattachements, type ProchainesAssociations,
  type RattachementDeLaSoiree, type RattachementsAVenir, type ResumeRattachees,
} from './rattachement-model.js'

/** La fiche d'une association (Exploitation › Associations). */
const cheminAssociation = (id: string) => `/exploitation/associations/${id}`
/** « À décider », ouvert sur un cas : la cible de l'alerte « avant la nuit ». */
const cheminADecider = '/exploitation/associations?vue=a-decider'
/** Là où « À rattacher » reste pour qui ne consulte pas les associations. */
const CHEMIN_REGLAGES_BILLETTERIES = '/settings/soirees/billetteries'

/** « · Médecine » — un lien vers chaque association, hors de tout autre lien. */
export function NomsAssociations({ associations, prefixe = ' · ', liens = true }: {
  associations: readonly AssociationCourte[] | undefined; prefixe?: string
  /** `false` : le nom seul, dans une carte qui est déjà un lien (un lien dans un lien n'est pas valide). */
  liens?: boolean
}) {
  if (!associations || associations.length === 0) return null
  return (
    <span className="so-assos">
      {prefixe}
      {associations.map((a, i) => (
        <span key={a.id}>{i > 0 ? ', ' : ''}{liens ? <Link href={cheminAssociation(a.id)} className="so-assos__lien">{a.name}</Link> : a.name}</span>
      ))}
    </span>
  )
}
import { ligneVenue, type VenueFormules } from './formules-model.js'

/**
 * RATTACHER UN ÉVÉNEMENT D'ASSOCIATION À LA SOIRÉE DU CLUB (19/10/2026).
 *
 * Quatre surfaces, un seul jeu de gestes :
 *   · « À rattacher » — ce que Gestion demande (Exploitation ›
 *     Associations › À décider depuis le 20/10/2026, et en panneau depuis le
 *     bandeau des Soirées) ;
 *   · la sous-ligne des listes et du calendrier, avec « Défaire » ;
 *   · le bloc « Billetteries de la soirée » (fiche d'une soirée du club) ;
 *   · le bandeau d'un événement Pretix qui renvoie vers sa soirée.
 *
 * Chaque geste est immédiat et s'annule d'un clic (« ANNULER » dans le
 * message qui suit — jamais « Défaire » : après un « Défaire », le même mot
 * aurait voulu dire « rattacher de nouveau ») : le serveur rend ses
 * décisions, l'annulation les révoque et rétablit celle d'avant.
 *
 * Après chaque geste, toutes les listes ouvertes se relisent
 * (`EVT_RATTACHEMENTS`) : dans « À décider », « À rattacher » et la nuit des
 * événements d'associations ne se contredisent plus.
 */

/** Dit à toutes les listes ouvertes de se relire (un geste, une correction de nuit). */
export function signalerRattachements(): void {
  try { window.dispatchEvent(new Event(EVT_RATTACHEMENTS)) } catch { /* hors navigateur */ }
}

type Reponse = { ok?: boolean; decisions?: string[]; message?: string; error?: string }

/** Les gestes, avec le message et son « Défaire ». */
export function useGestesRattachement(apres: () => void) {
  const toast = useToast()
  const annuler = useCallback(async (decisions: string[]) => {
    const r = await adminMutate<Reponse>('POST', '/api/v1/soirees/rattachements/annuler', { decisions })
    if (r.status !== 200) { toast('error', r.data.message ?? 'Annulation impossible.'); return }
    toast('ok', r.data.message ?? 'Geste annulé.')
    apres()
    signalerRattachements()
  }, [toast, apres])
  const conclure = useCallback((r: { status: number; data: Reponse }): string | null => {
    if (r.status !== 200) return r.data.message ?? 'Geste impossible.'
    const ids = r.data.decisions ?? []
    // « ANNULER », la convention des messages de Gestion (planning) : rétablit l'état d'avant ce geste.
    toast('ok', r.data.message ?? 'Fait.', ids.length > 0 ? { action: { label: 'ANNULER', onAction: () => { void annuler(ids) } } } : undefined)
    apres()
    signalerRattachements()
    return null
  }, [toast, apres, annuler])
  return {
    rattacher: async (enfants: string[], parent_id: string, motif: string | null) =>
      conclure(await adminMutate<Reponse>('POST', '/api/v1/soirees/rattachements/rattacher', { enfants, parent_id, motif })),
    separer: async (enfants: string[], motif: string | null = null) =>
      conclure(await adminMutate<Reponse>('POST', '/api/v1/soirees/rattachements/separer', { enfants, motif })),
    annuler,
  }
}

/* ═══════════════════════════════════════ un élément « À rattacher » ═══ */

/** Un événement (ou un groupe : les huit INTERSANTE) à rattacher : la soirée proposée, les raisons, trois gestes. */
export function CarteARattacher({ item, motifMin, peutGerer, onFait, alerte = null, ancre = false, sansSeparer = false, ouCorrigerLaNuit }: {
  item: ItemARattacher; motifMin: number; peutGerer: boolean; onFait: () => void
  /**
   * Où se corrige la nuit d'un événement, vu d'ici (revue du 20/10/2026) :
   * dans « À décider », la liste est PLUS BAS sur la même page — renvoyer à
   * Réglages › Billetteries, qui ne garde qu'un bouton vers « À décider »,
   * faisait tourner en rond. Par défaut : selon les droits de la personne.
   */
  ouCorrigerLaNuit?: string | undefined
  /** L'alerte « avant la nuit » de ce cas (20/10/2026) : « Prévenu le … », « Alerte prévue … ». */
  alerte?: EtatAlerteCas | null
  /** La carte porte l'ancre du cas : le lien de l'alerte défile jusqu'à elle (« À décider » seulement). */
  ancre?: boolean
  /**
   * Pas encore de soirée du club (09/09) : JAMAIS « Garder séparée » — une
   * séparation à la main est définitive, elle empêcherait le rattachement
   * automatique quand le club ouvrira sa soirée.
   */
  sansSeparer?: boolean
}) {
  const gestes = useGestesRattachement(onFait)
  const litAssociations = usePermissions().permissions.includes('association.read')
  const ouNuit = ouCorrigerLaNuit ?? (litAssociations ? 'Exploitation › Associations, « Corriger la nuit d’un événement d’association »' : 'Réglages Soirées › Billetteries')
  const [cible, setCible] = useState(item.suggestion?.id ?? item.choix[0]?.id ?? '')
  const [autre, setAutre] = useState(false)
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const choisie = item.choix.find((c) => c.id === cible) ?? null
  const ids = item.enfants.map((e) => e.id)
  const motifRequis = choisie !== null && !choisie.meme_nuit
  const bloquantes = item.raisons.filter((r) => !r.ok)
  const pour = item.raisons.filter((r) => r.ok)

  const agir = async (f: () => Promise<string | null>) => {
    setEnvoi(true); setErreur(null)
    try { setErreur(await f()) } finally { setEnvoi(false) }
  }

  return (
    <article className="so-ratt" aria-label={`${item.libelle} — ${nuitCourte(item.nuit)}`} id={ancre ? idDuCas(item.cle) : undefined}>
      <header className="so-ratt__tete">
        <span className="so-ratt__nom">{item.libelle} <span className="so-ratt__billetterie">({item.billetterie})</span></span>
        <span className="so-ratt__faits">
          {nuitCourte(item.nuit)} · {item.scanned ?? 0} entrées · {item.valid ?? 0} billets
          {item.enfants.length === 1 ? ` · annoncé ${jourFr(item.enfants[0]!.starts_at).toLowerCase()} à ${heureFr(item.enfants[0]!.starts_at)}` : ''}
        </span>
      </header>
      {item.separee ? (
        <p className="so-ratt__etat">
          Gardée séparée{item.separee.par ? ` par ${item.separee.par}` : ''} : Gestion ne la rattachera pas seul.
          {item.separee.raison ? ` « ${item.separee.raison} »` : ''}
        </p>
      ) : <p className="so-ratt__etat">{texteVerdict(item)}.</p>}
      {alerte && (alerte.etat === 'ENVOYEE' || alerte.etat === 'PREVUE' || alerte.etat === 'SANS_DESTINATAIRE') ? (
        <p className="so-ratt__alerte"><BellRing size={13} aria-hidden="true" /> {alerte.phrase}</p>
      ) : null}

      {choisie ? <p className="so-ratt__consequence">{phraseConsequence({ scanned: item.scanned, libelle: item.libelle, nombre: item.enfants.length }, choisie)}</p> : (
        <p className="so-ratt__consequence">Aucune soirée du club cette nuit-là ni les nuits voisines : l’événement reste une soirée à part. Si sa date est fausse, corrigez d’abord sa nuit ({ouNuit}).</p>
      )}

      {bloquantes.length > 0 ? (
        <ul className="so-ratt__raisons so-ratt__raisons--ko" aria-label="Pourquoi Gestion demande">
          {bloquantes.map((r) => <li key={r.texte}>{r.texte}</li>)}
        </ul>
      ) : null}
      {pour.length > 0 ? (
        <ul className="so-ratt__raisons" aria-label="Ce qui concorde">
          {pour.map((r) => <li key={r.texte}>{r.texte}</li>)}
        </ul>
      ) : null}

      {peutGerer && item.choix.length > 0 ? (
        <div className="so-ratt__gestes">
          {autre ? (
            <Field label="Soirée du club" helper="La nuit même d’abord, puis la veille et le lendemain.">
              <Select value={cible} onChange={setCible} ariaLabel={`Soirée à laquelle rattacher ${item.libelle}`}
                options={item.choix.map((c) => ({
                  value: c.id,
                  label: `${c.name} — ${c.meme_nuit ? 'même nuit' : nuitCourte(c.nuit).toLowerCase()} · ${heureFr(c.starts_at)}`,
                }))} />
            </Field>
          ) : null}
          {motifRequis || autre ? (
            <Field label={motifRequis ? 'Pourquoi' : 'Pourquoi (facultatif)'} error={erreur}
              helper={motifRequis ? `Une autre nuit : quelques mots (${motifMin} caractères au moins), la correction est tracée.` : 'Gardé avec la décision.'}>
              <TextInput value={motif} onChange={setMotif} maxLength={300} placeholder="Même soirée, annoncée à la mauvaise date"
                ariaLabel={`Motif pour ${item.libelle}`} />
            </Field>
          ) : erreur ? <p className="so-ratt__erreur" role="alert">{erreur}</p> : null}
          <div className="so-ratt__boutons">
            <button type="button" className="lc-btn lc-btn--primary" disabled={envoi || !choisie || (motifRequis && motif.trim().length < motifMin)}
              onClick={() => { if (choisie) void agir(() => gestes.rattacher(ids, choisie.id, motif.trim() || null)) }}>
              <Link2 size={14} aria-hidden="true" /> {envoi ? 'Enregistrement…' : `Rattacher à « ${choisie?.name ?? '…'} »`}
            </button>
            {!autre && item.choix.length > 1 ? (
              <button type="button" className="lc-btn lc-btn--ghost" onClick={() => setAutre(true)}>Choisir une autre soirée</button>
            ) : null}
            {!autre && item.choix.length === 1 ? (
              <button type="button" className="lc-btn lc-btn--ghost" onClick={() => setAutre(true)}>Ajouter un motif</button>
            ) : null}
            {!item.separee && !sansSeparer ? (
              <button type="button" className="lc-btn lc-btn--ghost" disabled={envoi}
                onClick={() => { void agir(() => gestes.separer(ids, motif.trim() || null)) }}>
                <Link2Off size={14} aria-hidden="true" /> Garder séparée
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  )
}

/* ═══════════════════════════════════════ « À rattacher » : la liste ═══ */

/**
 * La liste, relue à chaque geste (d'ici ou d'ailleurs sur la page) et toutes
 * les `relireMs` : le worker rattache seul toutes les 5 minutes, et un bandeau
 * qui ne se relit jamais annonçait encore « 3 à rattacher » après coup.
 * Une erreur se dit (« Chargement… » restait affiché pour toujours).
 */
export function useListeRattachements(relireMs = 60_000) {
  const [liste, setListe] = useState<ListeRattachements | null>(null)
  const [erreur, setErreur] = useState(false)
  const charger = useCallback(() => {
    void apiGet<ListeRattachements>('/api/v1/soirees/rattachements')
      .then((j) => {
        if (Array.isArray(j.a_rattacher)) { setListe(j); setErreur(false) } else setErreur(true)
      })
      .catch(() => setErreur(true))
  }, [])
  useEffect(() => {
    charger()
    const t = relireMs > 0 ? setInterval(charger, relireMs) : null
    window.addEventListener(EVT_RATTACHEMENTS, charger)
    return () => { if (t) clearInterval(t); window.removeEventListener(EVT_RATTACHEMENTS, charger) }
  }, [charger, relireMs])
  return { liste, erreur, charger }
}

/**
 * La liste « À rattacher », puis ce qui est déjà rattaché (avec « Défaire ») et
 * ce qui a été gardé séparé (pour revenir sur la décision).
 * `complet` : les trois parties (Réglages) ; sinon la première seule (panneau).
 */
export function ARattacher({ complet = false, onChange, ouCorrigerLaNuit }: {
  complet?: boolean; onChange?: () => void
  /** Où se corrige la nuit d'un événement, vu de la page qui montre la liste (`CarteARattacher`). */
  ouCorrigerLaNuit?: string | undefined
}) {
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const { liste, erreur, charger } = useListeRattachements()
  const apres = useCallback(() => { charger(); onChange?.() }, [charger, onChange])
  const gestes = useGestesRattachement(apres)

  if (liste === null) {
    return erreur ? (
      <p className="lc-hint" role="alert">
        La liste n’a pas pu être lue : rien n’est rattaché ni défait tant qu’elle ne s’affiche pas.{' '}
        <button type="button" className="lc-btn lc-btn--ghost" onClick={charger}>Réessayer</button>
      </p>
    ) : <p className="lc-hint">Chargement…</p>
  }
  const bientot = liste.bientot ?? []
  const seules = complet ? seulesAVenir(liste) : []
  return (
    <div className="so-ratts">
      {liste.a_rattacher.length === 0 ? (
        <p className="lc-hint">Rien à rattacher : chaque événement d’association est rattaché à sa soirée, gardé séparé, ou seul sur sa nuit.</p>
      ) : liste.a_rattacher.map((i) => (
        <CarteARattacher key={`${i.cle}-${i.enfants.length}`} item={i} motifMin={liste.motif_min} peutGerer={peutGerer} onFait={apres}
          alerte={alerteDuCas(liste.alertes, i.cle, 'A_RATTACHER')} ancre={complet} ouCorrigerLaNuit={ouCorrigerLaNuit} />
      ))}

      {seules.length > 0 ? (
        <>
          <h3 className="so-ratts__titre">Pas encore de soirée du club</h3>
          {/* Ce que Gestion fera quand le club ouvrira sa soirée dépend du RÉGLAGE (« jamais » : rien seul). */}
          <p className="lc-hint">{phraseSeules(liste.attach_mode)}</p>
          {seules.map((i) => (
            <CarteARattacher key={`seule-${i.cle}-${i.enfants.length}`} item={i} motifMin={liste.motif_min} peutGerer={peutGerer} onFait={apres}
              alerte={alerteDuCas(liste.alertes, i.cle, 'SEULE')} ancre sansSeparer ouCorrigerLaNuit={ouCorrigerLaNuit} />
          ))}
        </>
      ) : null}

      {complet && bientot.length > 0 ? (
        <>
          <h3 className="so-ratts__titre">Rattachés seuls dans quelques minutes</h3>
          <p className="lc-hint">La règle et le réglage le permettent : Gestion les rattache à son prochain passage (toutes les 5 minutes). Vous pouvez décider avant lui.</p>
          {bientot.map((i) => (
            <CarteARattacher key={`auto-${i.cle}-${i.enfants.length}`} item={i} motifMin={liste.motif_min} peutGerer={peutGerer} onFait={apres} ouCorrigerLaNuit={ouCorrigerLaNuit} />
          ))}
        </>
      ) : null}

      {complet && liste.rattachees.length > 0 ? (
        <>
          <h3 className="so-ratts__titre">Déjà rattachés</h3>
          <ul className="so-ratts__lignes">
            {liste.rattachees.map((r) => (
              <li key={`${r.parent.id}-${r.resume.source ?? r.resume.billetterie}`} className="so-ratts__ligne">
                <span className="so-ratts__texte">
                  <strong>{r.resume.libelle}</strong> ({r.resume.billetterie}) → <Link href={`/soirees/${r.parent.id}`}>{r.parent.name}</Link>
                  <span className="lc-hint"> · {nuitCourte(r.parent.nuit)} · {texteMethode(r.resume.methode, r.resume.nombre)}
                    {r.confiance === 'CERTAIN' ? ' (certain)' : r.confiance === 'PROBABLE' ? ' (probable)' : ''}
                    {r.decided_by ? ` par ${r.decided_by}` : ''}</span>
                </span>
                {peutGerer ? (
                  <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void gestes.separer(r.resume.ids ?? []) }}
                    aria-label={`Défaire : ${r.resume.libelle} ne compte plus avec ${r.parent.name} et redevient une soirée à part`}>Défaire</button>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {complet && liste.separees.length > 0 ? (
        <>
          <h3 className="so-ratts__titre">Gardés séparés</h3>
          {liste.separees.map((i) => (
            <CarteARattacher key={`sep-${i.cle}`} item={i} motifMin={liste.motif_min} peutGerer={peutGerer} onFait={apres} ouCorrigerLaNuit={ouCorrigerLaNuit} />
          ))}
        </>
      ) : null}
    </div>
  )
}

/* ═══════════════════════════════════ le bandeau de la page Soirées ═══ */

/** « 1 événement d’association à rattacher » — seulement quand Gestion demande, et pour qui décide. */
export function BandeauARattacher({ onChange }: { onChange?: () => void }) {
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const { liste, charger } = useListeRattachements()
  const [ouvert, setOuvert] = useState(false)
  // Seulement ce que personne d'autre ne décidera : ce que le worker rattache
  // à sa prochaine passe n'est pas « à rattacher ». Le panneau ouvert reste
  // ouvert quand le dernier élément vient d'être décidé.
  const n = liste?.a_rattacher.length ?? 0
  if (!peutGerer || (n === 0 && !ouvert)) return null
  return (
    <>
      <section className="so-ratt-bandeau" aria-label="Événements d’association à rattacher">
        <p className="so-ratt-bandeau__texte">
          {n > 0
            ? <><strong>{n} événement{n > 1 ? 's' : ''} d’association à rattacher</strong> : Gestion vous demande à quelle soirée du club {n > 1 ? 'ils appartiennent' : 'il appartient'}. Tant que rien n’est décidé, {n > 1 ? 'ils restent' : 'il reste'} une ligne à part.</>
            : <strong>Plus rien à rattacher.</strong>}
        </p>
        <button type="button" className="lc-btn" onClick={() => setOuvert(true)}>Voir</button>
      </section>
      <Drawer open={ouvert} title="À rattacher" onClose={() => { setOuvert(false); charger() }}>
        <ARattacher onChange={() => { charger(); onChange?.() }} />
        <p className="lc-hint so-ratt-bandeau__reglages">
          Ce que Gestion rattache seul se règle dans <Link href="/settings/soirees/billetteries">Réglages Soirées › Billetteries</Link>.
        </p>
      </Drawer>
    </>
  )
}

/* ═══════════════════════════════════ la sous-ligne des listes ═══ */

/** « + MEDECINE (Pretix) · rattachée automatiquement · Défaire » — hors de tout lien (un bouton dans un lien n'est pas valide). */
export function SousLigneRattachees({ resume, onChange, defaire = true }: { resume: ResumeRattachees[] | undefined; onChange?: () => void; defaire?: boolean }) {
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const gestes = useGestesRattachement(() => onChange?.())
  if (!resume || resume.length === 0) return null
  return (
    <div className="so-ratt-sous">
      {resume.map((r) => (
        <span key={`${r.source ?? r.billetterie}-${r.libelle}`} className="so-ratt-sous__ligne">
          <span>{libelleSousLigne(r)}</span>
          {/* Une carte-lien (« Dernier week-end ») passe `defaire={false}` : les noms y restent du texte. */}
          <NomsAssociations associations={r.associations} liens={defaire} />
          {defaire && peutGerer && r.ids && r.ids.length > 0 ? (
            <>
              {' · '}
              <button type="button" className="so-ratt-sous__defaire" onClick={() => { void gestes.separer(r.ids!) }}
                aria-label={`Défaire : ${r.libelle} ne compte plus avec cette soirée et redevient une soirée à part`}>Défaire</button>
            </>
          ) : null}
        </span>
      ))}
    </div>
  )
}

/* ═══════════════════════════════════ la fiche d'une soirée ═══ */

/**
 * LE BLOC « BILLETTERIES DE LA SOIRÉE » (soirée du club avec des événements
 * rattachés), ou LE BANDEAU d'un événement d'association : rattaché (vers sa
 * soirée), à rattacher (la proposition), gardé séparé.
 */
export function BilletteriesDeLaSoiree({ r, soiree, onChange, venue = null, associations = null }: {
  r: RattachementDeLaSoiree | null | undefined
  /** L'association de chaque événement (20/10/2026) : « · Médecine ». `null` : pas le droit, ou réglage éteint. */
  associations?: Readonly<Record<string, AssociationCourte>> | null
  soiree: { id: string; name: string; source: string; tickets_scanned: number | null; tickets_valid: number | null }
  onChange: () => void
  /** La venue des associations (19/10/2026) : « 249 billets · 224 entrés · 10 % absents » sur la ligne de chacune. `null` : réglage éteint. */
  venue?: VenueFormules | null
}) {
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const gestes = useGestesRattachement(onChange)
  const [proposer, setProposer] = useState(false)
  const motifMin = 8
  // Les libellés des billetteries (« Sans billetterie », « Billetterie Live Club »), jamais le code brut.
  const billetterie = useMemo(() => SOURCE_LABELS[soiree.source] ?? soiree.source, [soiree.source])
  if (!r) return null

  if (r.role === 'SOIREE') {
    if (r.enfants.length === 0) return null
    return (
      <section className="lc-panel gd-panel so-ratt-bloc" aria-labelledby="so-ratt-bloc-titre">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="so-ratt-bloc-titre">Billetteries de la soirée</h2></header>
        <div className="lc-panel__body">
          <p className="so-ratt-bloc__total">
            <strong>{(r.total?.scanned ?? 0).toLocaleString('fr-FR')}</strong> entrées · {(r.total?.valid ?? 0).toLocaleString('fr-FR')} billets, toutes billetteries
          </p>
          <ul className="so-ratt-bloc__lignes">
            <li className="so-ratt-bloc__ligne">
              <span className="so-ratt-bloc__nom">{soiree.name} <span className="so-ratt__billetterie">({billetterie})</span>
                <NomsAssociations associations={associations?.[soiree.id] ? [associations[soiree.id]!] : undefined} /></span>
              <span className="so-ratt-bloc__n">{soiree.tickets_scanned ?? 0} entrées</span>
              <span />
            </li>
            {r.enfants.map((e) => (
              <li key={e.id} className="so-ratt-bloc__ligne">
                <span className="so-ratt-bloc__nom"><Link href={`/soirees/${e.id}`}>{e.name}</Link> <span className="so-ratt__billetterie">({e.billetterie})</span>
                  <NomsAssociations associations={associations?.[e.id] ? [associations[e.id]!] : undefined} />
                  <span className="lc-hint"> · {texteMethode(e.methode, 1)}</span>
                  {/* Vendus et absents de cette association (la nuit finie) ; les entrées sont dans la colonne. */}
                  {(() => {
                    const a = venue?.associations.find((x) => x.id === e.id)
                    return a && venue ? <span className="so-ratt-bloc__venue">{ligneVenue(a, venue.fini, { lecture_complete: venue.lecture_complete, sans_entres: true })}</span> : null
                  })()}</span>
                {/* UN SEUL NOMBRE D'ENTRÉES (revue du 19/10/2026) : la ligne disait
                    « 230 entrés » (billets, en direct) à côté de « 226 entrées »
                    (agrégat recalculé à la synchro). La venue compte en direct :
                    elle fait foi quand elle est là ; sinon, l'agrégat. */}
                <span className="so-ratt-bloc__n">{(venue?.associations.find((x) => x.id === e.id)?.entres ?? e.scanned ?? 0).toLocaleString('fr-FR')} entrées</span>
                {peutGerer ? (
                  <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void gestes.separer([e.id]) }}
                    aria-label={`Défaire : ${e.name} ne compte plus avec ${soiree.name} et redevient une soirée à part`}>Défaire</button>
                ) : <span />}
              </li>
            ))}
          </ul>
          <p className="lc-hint">
            Les prévisions restent celles de la billetterie du club : les entrées des associations ne sont jamais prises pour
            un écart de prévision. La Comptabilité lit chaque billetterie à part.
          </p>
        </div>
      </section>
    )
  }

  if (r.role === 'RATTACHEE' && r.parent) {
    const p = r.parent
    return (
      <section className="so-ratt-bandeau so-ratt-bandeau--lie" aria-label="Soirée du club de cet événement">
        <p className="so-ratt-bandeau__texte">
          Rattachée à <Link href={`/soirees/${p.id}`}><strong>{p.name}</strong></Link> ({nuitCourte(p.nuit).toLowerCase()}) :
          ses entrées comptent avec cette soirée dans les listes et le calendrier. {texteMethode(p.methode, 1).replace(/^./, (c) => c.toUpperCase())}
          {p.confiance === 'CERTAIN' ? ' (certain)' : p.confiance === 'PROBABLE' ? ' (probable)' : ''}{p.decided_by ? ` par ${p.decided_by}` : ''}.
        </p>
        <span className="so-ratt__boutons">
          <Link href={`/soirees/${p.id}`} className="lc-btn">Ouvrir la soirée</Link>
          {peutGerer ? <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void gestes.separer([soiree.id]) }}>Défaire</button> : null}
        </span>
      </section>
    )
  }

  // SEULE : aucune soirée du club cette nuit-là — la fiche offre quand même
  // le rattachement à la main (la veille, le lendemain), quand il y en a une.
  if (r.item && (r.role === 'A_RATTACHER' || r.role === 'SEPAREE' || (r.role === 'SEULE' && r.item.choix.length > 0))) {
    return (
      <section className="so-ratt-bandeau" aria-label="Rattacher à une soirée du club">
        <p className="so-ratt-bandeau__texte">
          {r.role === 'SEPAREE'
            ? 'Gardée séparée : cette soirée d’association reste à part des soirées du club.'
            : r.role === 'SEULE'
              ? 'Aucune soirée du club cette nuit-là : elle reste une soirée à part. Si c’était la même soirée qu’une soirée de la veille ou du lendemain, rattachez-la.'
              : r.item.auto_bientot
                ? <>Gestion la rattache seul à <strong>{r.item.suggestion?.name}</strong> à son prochain passage (5 minutes au plus).</>
                : <>Pas encore rattachée{r.item.suggestion ? <> — Gestion propose <strong>{r.item.suggestion.name}</strong></> : null} : tant que rien n’est décidé, elle reste une ligne à part.</>}
        </p>
        {peutGerer ? <button type="button" className="lc-btn" onClick={() => setProposer((x) => !x)}>{proposer ? 'Fermer' : 'Rattacher à une soirée…'}</button> : null}
        {/* « Seule sur sa nuit » : jamais « Garder séparée » (revue du 20/10/2026) — une séparation à la
            main est définitive, et empêcherait le rattachement automatique quand le club ouvrira sa soirée. */}
        {proposer ? <div className="so-ratt-bandeau__carte"><CarteARattacher item={r.item} motifMin={motifMin} peutGerer={peutGerer} onFait={onChange} sansSeparer={r.role === 'SEULE'} /></div> : null}
      </section>
    )
  }
  return null
}

/* ═══════════════════════ annoncé AVANT la nuit (19/10/2026) ═══ */

/**
 * « MEDECINE le 15/10 sera rattachée à L'ÉTUDIANTE » — sur l'Accueil et dans
 * le Centre d'activité, tant que la nuit n'est pas passée (horizon réglé).
 * Gestion rattache dès que l'événement paraît dans Pretix, des jours avant :
 * l'erreur se corrige AVANT la soirée, d'un clic (« Défaire », annulable
 * juste après). Rien sans le droit de lire les soirées, rien quand le réglage
 * est éteint, rien quand il n'y a rien à annoncer.
 */
export function RattachementsAVenirPanel({ lieu }: { lieu: 'accueil' | 'activite' }) {
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  // « Prochaine soirée de Médecine » (20/10/2026) : pour qui consulte les associations.
  const litAssociations = permissions.permissions.includes('association.read')
  const [r, setR] = useState<RattachementsAVenir | null>(null)
  const [prochaines, setProchaines] = useState<ProchainesAssociations | null>(null)
  const charger = useCallback(() => {
    void apiGet<RattachementsAVenir>('/api/v1/soirees/rattachements/a-venir')
      .then((j) => { setR(Array.isArray(j.annonces) ? j : null) })
      .catch(() => setR(null))
    if (litAssociations) {
      void apiGet<ProchainesAssociations>('/api/v1/associations/prochaines')
        .then((j) => { setProchaines(j.actif && Array.isArray(j.prochaines) ? j : null) })
        .catch(() => setProchaines(null))
    }
  }, [litAssociations])
  useEffect(() => {
    charger()
    const t = setInterval(charger, 120_000)
    window.addEventListener(EVT_RATTACHEMENTS, charger)
    return () => { clearInterval(t); window.removeEventListener(EVT_RATTACHEMENTS, charger) }
  }, [charger])
  const gestes = useGestesRattachement(charger)
  const toast = useToast()
  // Un geste refusé (nuit finalisée, soirée annulée entre-temps) se dit ; il ne se tait pas.
  const faire = useCallback(async (p: Promise<string | null>) => { const e = await p; if (e) toast('error', e) }, [toast])
  const lignesAssos = prochaines?.prochaines ?? []
  const titre = titrePanneauAVenir(r, lignesAssos.length)
  if (!titre) return null
  return (
    <section className={`lc-panel gd-panel so-avenir so-avenir--${lieu}`} aria-labelledby={`so-avenir-${lieu}`}>
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id={`so-avenir-${lieu}`}><CalendarClock size={16} aria-hidden="true" /> {titre}</h2>
      </header>
      <div className="lc-panel__body lc-panel__body--flush">
        <ul className="so-avenir__liste">
          {lignesAssos.map((p) => (
            <li key={`asso-${p.association.id}`} className="so-avenir__ligne">
              <div className="so-avenir__texte">
                <span className="so-avenir__phrase"><Handshake size={14} aria-hidden="true" /> {phraseProchaineAssociation(p)}</span>
              </div>
              <span className="so-avenir__gestes">
                <Link href={cheminAssociation(p.association.id)} className="lc-btn lc-btn--ghost">Ouvrir l’association</Link>
              </span>
            </li>
          ))}
          {(r?.annonces ?? []).map((a) => (
            <li key={a.cle} className="so-avenir__ligne">
              <div className="so-avenir__texte">
                <span className="so-avenir__phrase">{a.phrase}</span>
                <span className="lc-hint">{a.detail}</span>
              </div>
              <span className="so-avenir__gestes">
                <Link href={`/soirees/${a.parent.id}`} className="lc-btn lc-btn--ghost">Ouvrir la soirée</Link>
                {peutGerer ? (
                  <>
                    {/* CONFIRMER retire l'annonce (revue du 19/10/2026) : le geste
                        « Rattacher » à la même soirée remplace l'automatique par
                        une décision à la main — que le panneau ne montre plus.
                        ANNULER, dans le message, rétablit l'automatique. */}
                    <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void faire(gestes.rattacher(a.enfants, a.parent.id, null)) }}
                      aria-label={`C’est la même soirée : ${a.libelle} comptera avec ${a.parent.name}, l’annonce disparaît`}>
                      <Link2 size={14} aria-hidden="true" /> C’est la même soirée
                    </button>
                    <button type="button" className="lc-btn" onClick={() => { void faire(gestes.separer(a.enfants)) }}
                      aria-label={`Défaire : ${a.libelle} ne comptera pas avec ${a.parent.name} et restera une soirée à part`}>
                      <Link2Off size={14} aria-hidden="true" /> {a.etat === 'BIENTOT' ? 'Garder séparée' : 'Défaire'}
                    </button>
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
        {r && r.a_rattacher > 0 ? (
          <p className="lc-hint so-pad">
            {r.a_rattacher} événement{r.a_rattacher > 1 ? 's' : ''} de ces nuits attend{r.a_rattacher > 1 ? 'ent' : ''} votre décision
            {/* « À rattacher » vit dans Exploitation › Associations › À décider depuis le 20/10/2026 ;
                sans « Consulter les associations », la même liste reste dans Réglages Soirées › Billetteries
                (revue du 20/10 : le panneau ne menait alors plus nulle part). */}
            {litAssociations ? <> : <Link href={cheminADecider}>À décider</Link>.</>
              : <> : <Link href={CHEMIN_REGLAGES_BILLETTERIES}>Réglages Soirées › Billetteries</Link>.</>}
          </p>
        ) : null}
      </div>
    </section>
  )
}
